/**
 * `ShopifyConnectPanel` — body of the `shopify` install-method tab
 * inside `InstallMethodTabs` (ADR-0042 §2). Extracted from the prior
 * `ConnectBackendStep` Shopify branch (the "WOULD EXPOSE" 5-skill list
 * + Connect Shopify button + post-OAuth polling). Preserves every fix
 * from ADR-0037 Slice 1:
 *
 *   - **Detection is a hint, never a gate.** The Connect button is
 *     ALWAYS reachable when this tab is active — there is no detection
 *     gating in this panel (the tab itself is the merchant's
 *     authoritative selection per ADR-0042 §2).
 *   - **Shopify-specific connector-status poll.** Uses
 *     `getConnectorStatus(site_id, 'shopify')` so a multi-rail site
 *     (Shopify + Stripe) doesn't surface the Stripe row alphabetically
 *     and miss the Shopify-connected confirmation.
 *   - **No stuck-'connecting' before nav.** `connectShopify()` does
 *     NOT flip `connectStatus` to `'connecting'` synchronously; it
 *     navigates the top-level browser away. On bfcache restore (the
 *     merchant pressed Back from a Shopify-side error page) the
 *     button stays clickable so a retry isn't gated by stale state.
 *
 * The `returnedFromOauth` URL-flag reflection + 30s poll budget +
 * `pollingRef` stale-closure defense all carry over verbatim.
 */

import { Check, CircleAlert, Download, RefreshCw, ShoppingBag } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import type { Site } from '@sill/types';

import { API_ORIGIN } from '../../app/env.js';
import { useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { downloadDiscoveryJson } from '../../lib/install-snippet.js';
import {
  buildShopifyInstallUrl,
  deriveShopFromDomain,
  isValidShopInput,
} from '../../lib/shopify-install.js';
import { Card, Collapsible } from '../../components/atoms/index.js';
import { SHOPIFY_SENTENCE, SHOPIFY_WHAT, SHOPIFY_WHAT_BODY } from './install-copy.js';
import { useShopifyOauthReturn } from '../../hooks/useShopifyOauthReturn.js';
import { ShopifyAgentRedirectStep, ShopifyAgentsMdStep } from './ShopifyDiscoverySteps.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface ShopifyConnectPanelProps {
  /** The active site. Required for the install URL + the
   *  connector-status poll. */
  readonly site: Site;
}

export function ShopifyConnectPanel({
  site,
}: ShopifyConnectPanelProps): JSX.Element {
  const C = useC();
  const toast = useToast();
  // ADR-0052 §4.2.3 — polling state lives in the shared
  // `useShopifyOauthReturn` hook (consumed by both this panel AND
  // `InstallVerifyStep` so onboarding can advance on the same poll
  // result). The local `connectStatus` mirrors the shared status with
  // an additional `'connecting'` value the merchant produces by
  // clicking Connect within the SAME session BEFORE the top-level
  // navigation fires (no longer used pre-nav per the preserved fix
  // below; kept for the error-tile branch and the bfcache-restore
  // case where the hook reports `'error'`).
  const oauthReturn = useShopifyOauthReturn(site.site_id);
  const [errorConnectStatus, setErrorConnectStatus] = useState<
    'idle' | 'error'
  >('idle');

  // Mirror the shared hook into the panel's existing terminal-state
  // names so the render branches below need no rewrite.
  const connectStatus =
    errorConnectStatus === 'error' ? 'error' : oauthReturn.connectStatus;
  const polling = oauthReturn.polling;

  useEffect(() => {
    if (oauthReturn.connectStatus === 'error') {
      setErrorConnectStatus('error');
    }
  }, [oauthReturn.connectStatus]);

  // Role / lifecycle re-check deny on the OAuth callback (the member
  // who began the install was demoted / suspended / deactivated inside
  // the 10-minute state TTL and the callback refused to persist the
  // credential). Surfaced via `?connect_error=role_insufficient` by the
  // Shopify callback's redirect-back. Fires once on mount, then strips
  // the one-shot flag so a re-render doesn't re-toast.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect_error') !== 'role_insufficient') return;
    toast({
      title: 'Could not finish connecting Shopify',
      body:
        'Your access changed while this connection was in progress, so it '
        + 'was not saved. Ask an owner or admin to connect again.',
      tone: 'coral',
    });
    setErrorConnectStatus('error');
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('connect_error');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    } catch {
      /* private-mode / sandbox iframe — degrade silently */
    }
  }, [toast]);

  const domain = site.domain ?? '';
  // If the site's own domain IS a Shopify tenant subdomain we can use
  // it directly; for a custom-domain Shopify store (e.g. brand.com)
  // we need the merchant to disclose their underlying *.myshopify.com.
  const derivedShop = useMemo(() => deriveShopFromDomain(domain), [domain]);
  const [shopInput, setShopInput] = useState<string>('');
  const trimmedShopInput = shopInput.trim();
  const shopInputValid =
    trimmedShopInput.length > 0 && isValidShopInput(trimmedShopInput);
  const effectiveShop = derivedShop ?? (shopInputValid ? trimmedShopInput : null);
  const needsShopInput = derivedShop === null;

  const connectShopify = (): void => {
    if (domain.length === 0) {
      toast({
        title: 'No site selected',
        body: 'Add a site first, then come back to this step.',
        tone: 'coral',
      });
      return;
    }
    if (effectiveShop === null) {
      toast({
        title: 'Shop domain required',
        body: 'Enter your Shopify store domain (e.g. your-store.myshopify.com) before connecting.',
        tone: 'coral',
      });
      return;
    }
    // PRESERVED FIX: do NOT flip `connectStatus` to 'connecting' here.
    // We navigate away immediately, and if Shopify rejects the
    // authorize (e.g. "Unauthorized Access" on a config mismatch) the
    // merchant presses Back — bfcache can restore this page with a
    // stale 'connecting' state that leaves the button stuck-disabled.
    // Staying 'idle' keeps the button clickable on return so they can
    // retry. (The post-OAuth success path flips to 'connecting' via
    // the URL-flag effect above, which only fires when the callback
    // actually came back.)
    const url = buildShopifyInstallUrl(API_ORIGIN, site.site_id, effectiveShop);
    // Top-level navigation — Shopify's authorize page MUST render in
    // the merchant's tab, not an embedded iframe.
    window.location.assign(url);
  };

  return (
    <Card className="p-5">
      {/* One line, mirroring the WooCommerce connect copy (founder
          2026-08-04: drop the skills list — the merchant just picked
          skills on the previous step — and the "read-only" caveat). */}
      <p
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          lineHeight: 1.55,
          margin: '0 0 10px',
          maxWidth: 560,
        }}
        data-testid="shopify-connect-lead"
      >
        {SHOPIFY_SENTENCE}
      </p>
      <div style={{ marginBottom: 14 }}>
        <Collapsible variant="inline" label={SHOPIFY_WHAT}>
          <p style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, lineHeight: 1.6, margin: 0, maxWidth: 560 }}>
            {SHOPIFY_WHAT_BODY}
          </p>
        </Collapsible>
      </div>
      {needsShopInput ? (
        <div className="mb-4">
          <label
            htmlFor="shopify-shop-input"
            style={{
              display: 'block',
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 6,
            }}
          >
            YOUR SHOPIFY STORE DOMAIN
          </label>
          <input
            id="shopify-shop-input"
            type="text"
            value={shopInput}
            onChange={(e) => setShopInput(e.target.value)}
            placeholder="your-store.myshopify.com"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-invalid={trimmedShopInput.length > 0 && !shopInputValid}
            style={{
              width: '100%',
              maxWidth: 360,
              padding: '8px 10px',
              background: C.surface2,
              border: `1px solid ${
                trimmedShopInput.length > 0 && !shopInputValid
                  ? C.coralFg
                  : C.border
              }`,
              borderRadius: 2,
              color: C.text,
              fontFamily: monoFont,
              fontSize: 12,
              outline: 'none',
            }}
          />
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 11,
              color: C.muted,
              lineHeight: 1.5,
              marginTop: 6,
            }}
          >
            {site.domain} uses a custom domain — enter your .myshopify.com
            store address so we install on the right store.
          </div>
        </div>
      ) : null}
      <button
        type="button"
        onClick={connectShopify}
        disabled={
          connectStatus === 'connecting'
          || connectStatus === 'connected'
          || domain.length === 0
          || effectiveShop === null
        }
        className="flex items-center gap-2 px-5 py-2.5"
        style={{
          background: connectStatus === 'connected' ? C.limeDim : C.lime,
          color: connectStatus === 'connected' ? C.limeFg : C.ink,
          fontFamily: sansFont,
          fontWeight: 600,
          fontSize: 14,
          borderRadius: 2,
          opacity:
            connectStatus === 'connecting'
            || domain.length === 0
            || effectiveShop === null
              ? 0.6
              : 1,
          border:
            connectStatus === 'connected' ? `1px solid ${C.limeFg}` : 'none',
        }}
        aria-label="Connect Shopify"
      >
        {connectStatus === 'connecting' ? (
          <>
            <RefreshCw
              size={14}
              style={{ animation: 'spin 1.4s linear infinite' }}
            />
            Connecting…
          </>
        ) : connectStatus === 'connected' ? (
          <>
            <Check size={14} /> Shopify connected
          </>
        ) : (
          <>
            <ShoppingBag size={14} /> Connect Shopify
          </>
        )}
      </button>
      {connectStatus === 'connected' ? (
        <div className="mt-4 p-3" style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 2 }}>
          <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted, marginBottom: 6 }}>
            WELL-KNOWN DISCOVERY
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 10 }}>
            Your agent page, agent card and catalog are on your store domain under{' '}
            <span style={{ fontFamily: monoFont }}>/apps/sill/</span>. Shopify keeps its own{' '}
            <span style={{ fontFamily: monoFont }}>/llms.txt</span>,{' '}
            <span style={{ fontFamily: monoFont }}>/agents.md</span> and checkout files; Sill does not change them.
          </div>
          <button
            type="button"
            onClick={() =>
              downloadDiscoveryJson({
                name: site.name,
                url: `https://${site.domain}`,
                site_key: site.site_key,
              })
            }
            className="flex items-center gap-2 px-3 py-1.5"
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.muted,
              fontFamily: sansFont,
              fontSize: 12,
              borderRadius: 2,
              cursor: 'pointer',
            }}
          >
            <Download size={12} />
            Download agent-card.json
          </button>
        </div>
      ) : null}
      {connectStatus === 'connected' && site.domain.length > 0 ? (
        <>
          <ShopifyAgentsMdStep domain={site.domain} />
          <ShopifyAgentRedirectStep />
        </>
      ) : null}
      {connectStatus === 'error' ? (
        <div
          className="mt-3 p-2 flex items-start gap-2"
          style={{
            background: C.coralDim,
            border: `1px solid ${C.coralFg}`,
            borderRadius: 2,
          }}
        >
          <CircleAlert
            size={12}
            color={C.coralFg}
            className="flex-shrink-0 mt-0.5"
          />
          <span
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.text,
              lineHeight: 1.4,
            }}
          >
            Shopify reported a connection error. You can retry the
            Connect flow or skip and reconnect later from site settings.
          </span>
        </div>
      ) : null}
      {polling ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 1.5,
            marginTop: 10,
          }}
        >
          CHECKING CONNECTION…
        </div>
      ) : null}
      {connectStatus === 'unconfirmed' ? (
        // ADR-0054 §0 — the 30s OAuth-return poll budget expired
        // without a confirmed connect. The button has automatically
        // fallen back to its idle "Connect Shopify" affordance (see
        // the disabled / label branches above); this is the subtle
        // status hint so the merchant isn't left wondering why the
        // poll just stopped. Common cause: a stale `?connected=shopify`
        // URL flag from a prior attempt where the callback never
        // landed.
        <div
          data-testid="shopify-connect-unconfirmed"
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.5,
            marginTop: 10,
          }}
        >
          Couldn't confirm the connection — try again.
        </div>
      ) : null}
    </Card>
  );
}
